import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { SideNav } from './SideNav';
import { TopBar } from './TopBar';
import { CommandPalette } from './CommandPalette';
import { ShortcutsOverlay } from './ShortcutsOverlay';
import { DemoTour } from '@/components/tour/DemoTour';
import { getAuthToken } from '@/lib/api';
import { BRIDGE_PATHS, isBridgePath } from '@/lib/bridge-paths';
// LoginPage (demo-login with 4 preset users) is preserved at
// /pages/LoginPage.tsx but no longer the default unauth landing
// post-Phase 4. The new /login route renders pages/auth/Login.tsx
// (real customer email/password sign-in). To access the demo-login
// for dev tenants, navigate to /login/demo (route to be added later
// when it's needed; currently demo-login is unreachable).

/**
 * Paths that handle their own auth (no session required to render).
 * Phase A - `/auth/bridge` IS the path that establishes a session
 * from a Core-minted bridge token, so we must not redirect-to-login
 * before it has a chance to run.
 * Phase 4 - `/first-login` and `/forgot-password` are part of the
 * customer-auth flow: tokenless entry that bootstraps a session.
 */
const UNAUTH_PATH_PREFIXES = [
  '/login',
  // Both bridge forms - spread from the shared constant so this list and the
  // render gate below cannot drift apart. If they did, a token would land on a
  // path one accepts and the other redirects away from, and the user would see
  // a login page while holding a valid session token.
  ...BRIDGE_PATHS,
  '/first-login',
  '/forgot-password',
  // Wave 6 (2026-05-27) - MFA enrol + challenge run BEFORE the
  // session JWT is minted; they carry a partial-auth token in
  // the URL fragment (#t=…). The standalone-auth gate below
  // skips them so the chrome doesn't render.
  '/auth/mfa-enroll',
  '/auth/mfa-challenge',
  // Workstream B (2026-07-12) - SSO landing. The SAML ACS / OIDC
  // callback redirects the browser here with a freshly-minted session
  // token in the URL fragment (#access_token=…); the page establishes
  // the session, so it must render before any auth gate runs.
  '/sso/landing',
];

function isUnauthPath(pathname: string): boolean {
  return UNAUTH_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function useAuth(): { token: string | null } {
  const [token, setToken] = useState<string | null>(() => getAuthToken());
  useEffect(() => {
    const onChange = (): void => setToken(getAuthToken());
    window.addEventListener('foctta:auth-changed', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('foctta:auth-changed', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);
  return { token };
}

export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { token } = useAuth();
  const routerState = useRouterState();
  const isLoginRoute = routerState.location.pathname.startsWith('/login');
  const isUnauthRoute = isUnauthPath(routerState.location.pathname);

  // Unauthenticated and not on an unauth-handled path → kick to
  // /login (the real customer login page from Phase 4). Hard navigate
  // so the URL changes - keeps the back button working sanely.
  if (!token && !isUnauthRoute) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.replace('/login');
      return null;
    }
    // Already on /login (this branch shouldn't normally hit because
    // isUnauthRoute would be true, but fall through safely if it does).
    return <Outlet />;
  }
  // If we land on /login but already authenticated, kick to home.
  if (token && isLoginRoute) {
    return <RedirectHome />;
  }

  // The Phase 4 auth pages (/login, /first-login, /forgot-password)
  // and the Phase A bridge page (/auth/bridge) render standalone -
  // no SideNav/TopBar chrome - so the sign-on hops feel seamless.
  const path = routerState.location.pathname;
  if (
    isBridgePath(path) ||
    path.startsWith('/auth/mfa-') ||
    path.startsWith('/sso/landing') ||
    path.startsWith('/login') ||
    path.startsWith('/first-login') ||
    path.startsWith('/forgot-password')
  ) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Aurora background - Stripe/Linear/Vercel signature */}
      <div className="aurora fixed inset-0 pointer-events-none -z-10" />
      {/* Mesh-grid overlay */}
      <div className="mesh-grid fixed inset-0 pointer-events-none opacity-40 -z-10" />

      <div className="flex h-screen">
        <SideNav collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopBar onOpenPalette={() => setPaletteOpen(true)} />
          <motion.main
            key="route-content"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
            className="flex-1 overflow-auto"
          >
            <Outlet />
          </motion.main>
        </div>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <ShortcutsOverlay />
      <DemoTour />
    </div>
  );
}

function RedirectHome(): null {
  const navigate = useNavigate();
  useEffect(() => {
    navigate({ to: '/' });
  }, [navigate]);
  return null;
}
